/**
 * Crisis / national-internet mode switch (owner 2026-10-17): a manual global toggle for the days the
 * Iran host loses all foreign access. ON → trading restricted to USDT ⇄ Toman, crypto deposit/withdraw
 * handled manually, a user banner. Entry and exit are manual (owner's decision); the card also shows a
 * connectivity hint (foreign rate feeds failing) so the operator knows when to flip it.
 */
import { Badge, Button, Card, SectionHeader } from '@hopex/ui';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api.ts';
import { CommandDialog } from '../lib/command.tsx';
import { Remote, useRemote } from '../lib/remote.tsx';

interface Status {
  on: boolean;
  reason: string | null;
  since: string | null;
  recentFeedErrors: number;
}

export function CrisisCard() {
  const r = useRemote<Status>('/crisis');
  const [open, setOpen] = useState(false);
  return (
    <Remote r={r}>
      {(d) => (
        <Card className="p-4 sm:p-5" data-crisis-card data-crisis-on={d.on ? 'true' : 'false'}>
          <SectionHeader id="crisis" title="حالت بحران (اختلال اینترنت بین‌الملل)" />
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <p className="m-0 flex items-center gap-2 text-[14px] font-bold">
                وضعیت:{' '}
                <Badge tone={d.on ? 'negative' : 'positive'}>{d.on ? 'فعال' : 'غیرفعال'}</Badge>
              </p>
              <p className="m-0 mt-2 text-[12.5px] leading-6 text-fg-muted">
                در حالت بحران فقط خرید و فروش تتر فعال است (با قیمت صرافی‌های ایرانی)؛ بقیه‌ی ارزها
                متوقف می‌شوند، واریز و برداشت رمزارز دستی انجام می‌شود و به کاربران بنر نشان داده
                می‌شود. ورود و خروج فقط دستی است.
              </p>
              {d.on && d.since && (
                <p className="m-0 mt-1.5 text-[12px] text-fg-muted">
                  از {new Date(d.since).toLocaleString('fa-IR')}
                  {d.reason ? ` · ${d.reason}` : ''}
                </p>
              )}
              {!d.on && d.recentFeedErrors > 0 && (
                <p className="m-0 mt-2 flex items-center gap-1.5 text-[12.5px] font-semibold text-[var(--hx-pending)]">
                  <AlertTriangle aria-hidden className="size-4" />
                  نشانه‌ی اختلال: {d.recentFeedErrors.toLocaleString('fa-IR')} خطای دریافت قیمت در ۵
                  دقیقه‌ی اخیر. اگر اینترنت بین‌الملل قطع است، حالت بحران را فعال کنید.
                </p>
              )}
            </div>
            <Button
              variant={d.on ? 'secondary' : 'danger'}
              onPress={() => setOpen(true)}
              icon={<ShieldAlert className="size-4" />}
              data-crisis-toggle
            >
              {d.on ? 'خروج از حالت بحران' : 'فعال کردن حالت بحران'}
            </Button>
          </div>
          {open && (
            <CommandDialog
              title={d.on ? 'خروج از حالت بحران' : 'فعال کردن حالت بحران'}
              tone={d.on ? 'primary' : 'danger'}
              confirmLabel={d.on ? 'خروج از حالت بحران' : 'فعال کردن'}
              data-crisis-dialog
              run={async (reason) => {
                await api.post('/crisis', { on: !d.on, reason });
              }}
              onClose={() => setOpen(false)}
              onDone={() => {
                setOpen(false);
                r.reload();
              }}
            >
              <p className="m-0 text-[13px] leading-7 text-fg-muted">
                {d.on
                  ? 'همه‌ی معاملات و واریز و برداشت‌ها به حالت عادی برمی‌گردند و بنر کاربران برداشته می‌شود.'
                  : 'فقط خرید و فروش تتر فعال می‌ماند. بقیه‌ی ارزها متوقف، واریز و برداشت رمزارز دستی، و بنر اختلال به کاربران نشان داده می‌شود.'}
              </p>
            </CommandDialog>
          )}
        </Card>
      )}
    </Remote>
  );
}
